import { useMutation } from '@tanstack/react-query';
import { Alert, Button, Typography } from 'antd';
import React, { useState } from 'react';
import { ExportOutlined } from '@ant-design/icons';
import { type IntegrationInstallInstructionsProps } from '@pages/AdminSettings/components/IntegrationTab/constants';
import {
  SetupHelp,
  SetupLayout,
  SetupSteps
} from '@pages/AdminSettings/components/IntegrationTab/setup-layout';
import { MONDAY_CLIENT_ID } from '@config/constants';
import { apiClient } from '@api/client';
import { IntegrationsMondayIcon } from '@icons';

const { Link } = Typography;

const MONDAY_INSTALL_URL = `https://auth.monday.com/oauth2/authorize?client_id=${MONDAY_CLIENT_ID}&response_type=install`;

export default function MondayInstallInstructions({
  organizationId,
  onClose
}: IntegrationInstallInstructionsProps) {
  const [notFound, setNotFound] = useState(false);

  const verify = useMutation({
    mutationFn: async () => {
      const { data } =
        await apiClient.integration.integrationControllerGetInsalledRegistrations(
          { organizationId }
        );
      return data.integrations.some(
        (i) => i.type === 'monday' && i.status === 'installed'
      );
    },
    onSuccess: (installed) => {
      if (installed) {
        onClose();
      } else {
        setNotFound(true);
      }
    }
  });

  return (
    <SetupLayout
      logo={IntegrationsMondayIcon}
      title="Connect monday.com"
      subtitle="monday.com workflows create approvals in StreamWork and react to StreamWork events."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            loading={verify.isLoading}
            onClick={() => {
              setNotFound(false);
              verify.mutate();
            }}
          >
            Done — verify connection
          </Button>
        </>
      }
    >
      <SetupSteps
        steps={[
          {
            title: 'Install the StreamWork app in monday.com',
            action: (
              <Button
                type="primary"
                icon={<ExportOutlined />}
                onClick={() => window.open(MONDAY_INSTALL_URL, '_blank')}
              >
                Open monday.com
              </Button>
            )
          },
          {
            title: 'Add a StreamWork action to a workflow',
            description: (
              <>
                Create a workflow, or open one, with an action StreamWork
                provides, such as <strong>Create Approval</strong>.{' '}
                <Link
                  href="https://support.monday.com/hc/en-us/articles/11065311570066-Get-started-with-AI-workflows"
                  target="_blank"
                >
                  New to workflows?
                </Link>
              </>
            )
          },
          {
            title: 'Link your StreamWork account',
            description: (
              <>
                In the action&apos;s settings, click the{' '}
                <strong>StreamWork account</strong> field and sign in. This is
                what connects monday.com to StreamWork.
              </>
            )
          },
          {
            title: 'Save the workflow',
            description: (
              <>
                Then come back here and click{' '}
                <strong>Done — verify connection</strong>.
              </>
            )
          }
        ]}
      />

      {notFound && (
        <Alert
          type="warning"
          showIcon
          message="Connection not detected yet"
          description="It looks like step 3 isn't done. In your monday.com workflow, click the StreamWork account field and finish signing in, then try again."
        />
      )}

      <SetupHelp subject="monday.com" />
    </SetupLayout>
  );
}
